import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, FolderKanban, ClipboardList, CheckSquare,
  BookOpen, SlidersHorizontal, Users, Bell, LogOut, Menu, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV_BY_ROLE = {
  site_engineer: ["dashboard", "projects", "my_indents", "new_indent"],
  budget_controller: ["dashboard", "projects", "my_indents"],
  engineering_head: ["dashboard", "approvals", "projects", "sor", "thresholds"],
  chairman: ["dashboard", "approvals", "projects"],
  admin: ["dashboard", "projects", "sor", "thresholds", "users"],
};

const NAV = {
  dashboard: { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  projects: { to: "/projects", label: "Projects", icon: FolderKanban, testId: "nav-projects" },
  my_indents: { to: "/indents", label: "My Indents", icon: ClipboardList, testId: "nav-indents" },
  new_indent: { to: "/indents/new", label: "New Indent", icon: ClipboardList, testId: "nav-new-indent" },
  approvals: { to: "/approvals", label: "Approvals", icon: CheckSquare, testId: "nav-approvals" },
  sor: { to: "/sor", label: "SOR Master", icon: BookOpen, testId: "nav-sor" },
  thresholds: { to: "/thresholds", label: "Thresholds", icon: SlidersHorizontal, testId: "nav-thresholds" },
  users: { to: "/users", label: "Users", icon: Users, testId: "nav-users" },
};

function RoleLabel({ role }) {
  const map = {
    site_engineer: "Site Engineer",
    budget_controller: "Budget Controller",
    engineering_head: "Engineering Head",
    chairman: "Chairman",
    admin: "Admin",
  };
  return <span>{map[role] || role}</span>;
}

function NavItems({ items, onClick }) {
  return (
    <nav className="flex flex-col gap-1 px-3">
      {items.map((k) => {
        const item = NAV[k];
        if (!item) return null;
        const Icon = item.icon;
        return (
          <NavLink
            key={k}
            to={item.to}
            end
            data-testid={item.testId}
            onClick={onClick}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-[#0D3B66] text-white"
                  : "text-slate-600 hover:bg-slate-100 hover:text-[#0D3B66]"
              }`
            }
          >
            <Icon className="h-4 w-4" />
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/notifications"); setItems(data || []); } catch {}
  };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, []);
  const unread = items.filter((i) => !i.read).length;
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <div className="relative">
      <button
        data-testid="notifications-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
      >
        <Bell className="h-5 w-5 text-slate-600" />
        {unread > 0 && (
          <span data-testid="notifications-unread-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F97316] text-white text-[10px] font-semibold grid place-items-center">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-[360px] max-h-[70vh] overflow-auto bg-white rounded-lg border border-slate-200 shadow-lg z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="font-semibold text-slate-800">Notifications</div>
            <button data-testid="notifications-mark-all-read" onClick={markAll} className="text-xs text-[#0D3B66] hover:underline">Mark all read</button>
          </div>
          {items.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center">No notifications</div>
          ) : (
            <ul>
              {items.map((n) => (
                <li key={n.id} className={`px-4 py-3 border-b border-slate-100 text-sm ${n.read ? "" : "bg-amber-50/40"}`}>
                  <NotificationText n={n} />
                  <div className="text-[11px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationText({ n }) {
  switch (n.type) {
    case "budget_submitted":
      return <>Budget v{n.budget_version} submitted for <b>{n.project_name}</b> · ₹{Number(n.amount).toLocaleString("en-IN")}</>;
    case "budget_approved":
      return <>Your budget v{n.budget_version} for <b>{n.project_name}</b> was <span className="text-emerald-700 font-medium">approved</span></>;
    case "budget_rejected":
      return <>Your budget v{n.budget_version} for <b>{n.project_name}</b> was <span className="text-rose-700 font-medium">rejected</span>. {n.remark && <em>“{n.remark}”</em>}</>;
    case "indent_pending":
      return <>New indent · <b>{n.iow_code}</b> {n.description} · Qty {n.qty} · ₹{Number(n.value).toLocaleString("en-IN")} {n.budget_flag && <span className="text-amber-700">⚠ budget {n.cum_pct}%</span>}</>;
    case "indent_auto_approved":
      return <>Your indent for <b>{n.iow_code}</b> · ₹{Number(n.value).toLocaleString("en-IN")} was <span className="text-emerald-700 font-medium">auto-approved</span></>;
    case "indent_approved":
      return <>Your indent for <b>{n.iow_code}</b> · ₹{Number(n.value).toLocaleString("en-IN")} was <span className="text-emerald-700 font-medium">approved</span></>;
    case "indent_rejected":
      return <>Your indent for <b>{n.iow_code}</b> was <span className="text-rose-700 font-medium">rejected</span>. {n.remark && <em>“{n.remark}”</em>}</>;
    default: return <>{JSON.stringify(n)}</>;
  }
}

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const items = NAV_BY_ROLE[user?.role] || [];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 lg:px-6 h-16">
          <div className="flex items-center gap-3">
            <Sheet>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-btn" className="lg:hidden p-2 rounded-md hover:bg-slate-100">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[260px]">
                <div className="h-16 flex items-center px-4 border-b border-slate-100">
                  <BrandMark />
                </div>
                <div className="py-4">
                  <NavItems items={items} onClick={() => {}} />
                </div>
              </SheetContent>
            </Sheet>
            <div className="hidden lg:flex"><BrandMark /></div>
          </div>
          <div className="flex items-center gap-2 lg:gap-4">
            <NotificationBell />
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span data-testid="user-name" className="text-sm font-semibold text-slate-800">{user?.name}</span>
              <span data-testid="user-role" className="text-xs text-slate-500"><RoleLabel role={user?.role} /></span>
            </div>
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); nav("/login"); }}
              className="p-2 rounded-lg hover:bg-slate-100"
              title="Logout"
            >
              <LogOut className="h-5 w-5 text-slate-600" />
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar (desktop) */}
        <aside className="hidden lg:flex flex-col w-[240px] shrink-0 border-r border-slate-200 min-h-[calc(100vh-4rem)] bg-white">
          <div className="py-6">
            <NavItems items={items} />
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0">
          <div className="px-4 lg:px-8 py-6 lg:py-8 max-w-[1400px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function BrandMark() {
  return (
    <div className="flex items-center gap-2">
      <img
        src="/brand/radiance-mark.png"
        alt="Radiance EPC"
        className="h-9 w-9 object-contain"
        data-testid="brand-mark"
      />
      <div className="flex flex-col leading-none">
        <span className="text-[15px] font-bold tracking-tight text-[#0D3B66]">RADIANCE</span>
        <span className="text-[10px] font-semibold tracking-[0.2em] text-[#0D3B66]/70">EPC</span>
      </div>
    </div>
  );
}
